import {
  Box,
  Grid,
  GridItem,
  HStack,
  Link,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react';

import type { GetSentimentDataQuery } from '@revelio/data-access';

import { CompanyPeekTooltip } from '../company-details/CompanyPeekTooltip';
import type { CompanyMetrics } from '../hooks/useCompetitorComposition';
import type { EmployerHealth, RiskLevel } from '../hooks/useEmployerHealth';
import type { RoleLevel } from '../hooks/useResolveProfileIds';
import { StatBox } from '../salary/StatBox';
import { RISK_COLORS, TREND_COLORS } from '../utils/colors';
import { SentimentEffectsChart } from './SentimentEffectsChart';

const RISK_LABELS: Record<RiskLevel, string> = {
  low: 'Low Risk',
  medium: 'Moderate Risk',
  high: 'High Risk',
};

const TREND_ARROWS: Record<string, string> = {
  growing: '\u2191',
  stable: '\u2192',
  declining: '\u2193',
};

const slugifyForLinkedIn = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

interface EmployerHealthSummaryProps {
  health: EmployerHealth | undefined;
  companyName: string | null;
  sentimentEffectsData: GetSentimentDataQuery | undefined;
  sentimentEffectsLoading: boolean;
  isLoading: boolean;
  currentCompanyMetrics?: CompanyMetrics;
  sentimentOverall?: number | null;
  userSalary?: number;
  userRoleLevel?: RoleLevel | null;
}

export const EmployerHealthSummary = ({
  health,
  companyName,
  sentimentEffectsData,
  sentimentEffectsLoading,
  isLoading,
  currentCompanyMetrics,
  sentimentOverall,
  userSalary = 0,
  userRoleLevel,
}: EmployerHealthSummaryProps) => {
  if (isLoading) {
    return <Skeleton height="140px" borderRadius="md" />;
  }

  if (!health) {
    return (
      <Box bg="gray.50" p={4} borderRadius="md" textAlign="center">
        <Text color="gray.500" fontSize="sm">
          No employer health data available for {companyName ?? 'this company'}.
        </Text>
      </Box>
    );
  }

  const growthPct = (health.growthPotential * 100).toFixed(1);
  const linkedInSlug = companyName ? slugifyForLinkedIn(companyName) : null;

  let growthColor = 'gray.500';
  if (Number(growthPct) > 0) growthColor = 'green.500';
  else if (Number(growthPct) < 0) growthColor = 'red.500';

  return (
    <VStack align="stretch" spacing={3}>
      <HStack justify="space-between" align="baseline">
        <CompanyPeekTooltip
          data={{
            rcid: health.companyId,
            name: companyName ?? health.shortName,
            headcount: currentCompanyMetrics?.headcount ?? null,
            salary: currentCompanyMetrics?.salary ?? null,
            growthRate: currentCompanyMetrics?.growthRate ?? null,
            stabilityScore: health.stabilityScore,
            risk: health.risk,
            sentimentOverall: sentimentOverall ?? null,
            linkedInUrl: linkedInSlug
              ? `https://www.linkedin.com/company/${linkedInSlug}`
              : null,
          }}
          userSalary={userSalary}
          userRoleLevel={userRoleLevel ?? null}
        >
          <Text
            fontSize="xs"
            fontWeight="semibold"
            color="gray.600"
            cursor="pointer"
            _hover={{ textDecoration: 'underline' }}
          >
            Current Employer: {companyName}
          </Text>
        </CompanyPeekTooltip>
        {linkedInSlug && (
          <Link
            href={`https://www.linkedin.com/company/${linkedInSlug}`}
            isExternal
            fontSize="xs"
            color="blue.500"
            fontWeight="medium"
          >
            View on LinkedIn{' '}
            {/** biome-ignore lint/style/useConsistentCurlyBraces: migrated from develop */}
            {'\u2192'}
          </Link>
        )}
      </HStack>
      <Grid templateColumns="repeat(3, 1fr)" gap={3}>
        <GridItem>
          <StatBox
            label="Stability Score"
            value={`${health.stabilityScore}/100`}
            color={RISK_COLORS[health.risk]}
            subtext={RISK_LABELS[health.risk]}
          />
        </GridItem>
        <GridItem>
          <StatBox
            label="Hiring Velocity"
            value={`${TREND_ARROWS[health.hiringVelocity] ?? '\u2192'} ${health.hiringVelocity}`}
            color={TREND_COLORS[health.hiringVelocity] ?? 'gray.500'}
          />
        </GridItem>
        <GridItem>
          <StatBox
            label="Growth Potential"
            value={`${Number(growthPct) > 0 ? '+' : ''}${growthPct}%`}
            color={growthColor}
          />
        </GridItem>
      </Grid>

      {/* Sentiment: Factors of Hiring / Factors of Attrition chart */}
      <SentimentEffectsChart
        data={sentimentEffectsData}
        isLoading={sentimentEffectsLoading}
        companyName={companyName}
      />

      <Text fontSize="xs" color="gray.500">
        {health.summaryText}
      </Text>
    </VStack>
  );
};
